import { FeatureFlagNameEnum } from "@/services/internalApiV1";
import type { RouteLocationRaw, RouteRecordRaw } from "vue-router";

const INDEX: RouteLocationRaw = { name: "shared-folders-index" };

/**
 * Without a school licence the Gemeinsame Ablage is paused, and the paused
 * state lives on the overview only (#13478) — its card, its code modal and the
 * page turning live in place. A saved link to a folder or the Papierkorb is
 * sent there instead of opening a page whose requests the server refuses
 * (#13481): the folder page would show a bare 403, the trash page a flash of
 * "Deine Schule stellt dir diese Ablage nicht bereit".
 *
 * A guard rather than a check on the page, so nothing mounts and no request
 * goes out. Synchronous: the user is seeded from `data-user` in `main.ts`.
 * `?school=` is dropped on purpose — the paused overview has no school select.
 * The other refusals (no school, opted out) need the schools request and stay
 * with the pages.
 */
export const redirectWithoutSchoolLicense = (): true | RouteLocationRaw =>
  useUserStore().hasSchoolLicense ? true : INDEX;

export const sharedFoldersRoutes: RouteRecordRaw[] = [
  {
    path: "/shared-folders",
    name: "shared-folders-index",
    component: () => import("./SharedFolderListPage.vue"),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
    },
  },
  // Static route before the dynamic `:id`, or "discarded" resolves as a folder
  // id and the detail page asks the API for a node that cannot exist.
  {
    path: "/shared-folders/discarded",
    name: "shared-folders-discarded",
    component: () => import("./discarded/SharedFolderDiscardedPage.vue"),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
    },
    // Flag off, the footer link is dormant — so is the URL. `beforeEnter`
    // rather than a `meta.requiresFeature` hook: one route needs it so far.
    // The user is seeded from `data-user` in `main.ts`, so the flag answers
    // synchronously here.
    beforeEnter: () =>
      useUserStore().isFeatureEnabled(FeatureFlagNameEnum.SHARED_LIBRARY_TRASH)
        ? redirectWithoutSchoolLicense()
        : INDEX,
  },
  {
    path: "/shared-folders/:id",
    name: "shared-folders-show",
    component: () => import("./[id]/SharedFolderDetailPage.vue"),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
    },
    beforeEnter: redirectWithoutSchoolLicense,
  },
];
